import { FormValidationStatus } from '@customTypes/FormValidationStatus'
import clsx from 'clsx'
import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react'
import s from './Checkbox.module.scss'
interface Props {
  indeterminate?: boolean
  validationStatus?: FormValidationStatus
  value?: string | number | boolean | undefined
}
type NativeAttrs = Omit<React.ComponentPropsWithoutRef<'input'>, keyof Props>

export type CheckboxProps = Props & NativeAttrs

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      className,
      validationStatus,
      value,
      required,
      disabled,
      indeterminate,
      ...props
    },
    forwardedRef,
  ) => {
    // Handle ref forwarding
    const ref = useRef<HTMLInputElement>(null)
    useImperativeHandle(forwardedRef, () => ref.current)

    useLayoutEffect(() => {
      if (ref.current) {
        ref.current.indeterminate = indeterminate || false
      }
    }, [indeterminate, checked, ref])

    useEffect(() => {
      const { current: checkbox } = ref
      if (!checkbox) {
        return
      }

      if (indeterminate) {
        checkbox.setAttribute('aria-checked', 'mixed')
      } else {
        checkbox.setAttribute(
          'aria-checked',
          checkbox.checked ? 'true' : 'false',
        )
      }
    })

    return (
      <input
        className={clsx(s.checkbox, className)}
        type="checkbox"
        disabled={disabled}
        aria-invalid={validationStatus === 'error' ? 'true' : undefined}
        aria-required={required}
        ref={ref}
        value={value?.toString()}
        checked={indeterminate ? false : checked}
        placeholder=" "
        {...props}
      />
    )
  },
)

Checkbox.displayName = 'Checkbox'
export default Checkbox
